import AcknowledgementNotice from 'components/AcknowledgementNotice/AcknowledgementNotice';
import BadgeWithIcon from 'tailwind-components/BadgeWithIcon/BadgeWithIcon';
import Button from 'tailwind-components/Button/Button';
import Input from 'tailwind-components/Input/Input';
import Modal from 'tailwind-components/Modal/Modal';
import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useAppointmentActions from 'views/MyAppointments/AppointmentDetails/useAppointmentActions';
import { Box, Tooltip } from '@mui/material';
import { sanitizeDirtyHTML } from 'helpers/strings';
import { Clock, Info, TriangleAlert } from 'lucide-react';
import { cn } from 'lib/utils';
import { getSubstatusTooltip } from './helpers';
import CarrierAppointmentVideo from 'views/MyAppointments/CarrierAppointmentVideo/CarrierAppointmentVideo';
import {
  getStallAccentBarClass,
  getStalledDaysSinceInProgress,
  isNewAppointmentRequestType,
  isRecertificationRequest,
  isSubstatusActionable
} from 'helpers/appointments/appointments';
import ChevronIcon from 'icons/Chevron';

function getInProgressStallNudge(stalledDays, appointmentStatus) {
  if (appointmentStatus !== 'In Progress') return null;
  if (stalledDays == null) return null;
  if (stalledDays >= 21) return 'expiring';
  if (stalledDays >= 14 && stalledDays <= 20) return 'actSoon';
  return null;
}

function getStatePills(appointmentDetails, carrierConsidersStates) {
  // recertification requests have no state subform, show a fixed label instead
  if (isRecertificationRequest(appointmentDetails)) return ['Recertification'];
  if (!carrierConsidersStates) return [];
  if (appointmentDetails.carrier === 'BCBS') {
    return appointmentDetails.uniqueBcbsState
      ? [appointmentDetails.uniqueBcbsState]
      : [];
  }
  const states = (appointmentDetails.appointments_information || [])
    .map(row => row.Appointment_State_1)
    .filter(Boolean);
  return [...new Set(states)];
}

export default function AppointmentCard({
  appointmentDetails,
  carrierConsidersStates
}) {
  const {
    isLoading,
    credentials,
    errors,
    action,
    isAcknowledgementOpen,
    isAcknowledgementAccepted,
    actionOptions,
    setAction,
    setIsAcknowledgementOpen,
    setIsAcknowledgementAccepted,
    closeRequestSelectValue,
    setCloseRequestSelectValue,
    handleCredentialChange,
    handleSubmit,
    resetAcknowledgement,
    resetCredentialErrors
  } = useAppointmentActions(appointmentDetails, carrierConsidersStates);

  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);

  const carriersArray = useSelector(state => state.appointments.carriersArray);
  const newAppointmentsVideoURL = useMemo(() => {
    const carrier = carriersArray?.find(
      c => c.name === appointmentDetails.carrier
    );
    return carrier?.newAppointmentsVideoURL;
  }, [carriersArray, appointmentDetails.carrier]);

  const handleActionClick = selectedAction => {
    setAction(selectedAction);
    if (selectedAction.acknowledgement) {
      setIsAcknowledgementOpen(true);
    } else {
      handleSubmit(null, () => {});
    }
  };

  const statePills = useMemo(
    () => getStatePills(appointmentDetails, carrierConsidersStates),
    [appointmentDetails, carrierConsidersStates]
  );

  const selectedCloseReasonOption = useMemo(() => {
    if (action?.value !== 'close_request' || !action?.selectOptions?.length) {
      return null;
    }
    return (
      action.selectOptions.find(o => o.value === closeRequestSelectValue) ||
      null
    );
  }, [action, closeRequestSelectValue]);

  const hasNote = !!appointmentDetails.custom_note_for_agent;
  const showNewAppointmentVideo =
    !!newAppointmentsVideoURL &&
    isNewAppointmentRequestType(appointmentDetails.request_type);

  const stalledDays = useMemo(
    () => getStalledDaysSinceInProgress(appointmentDetails.in_progress_date),
    [appointmentDetails.in_progress_date]
  );
  const accentBarClass = getStallAccentBarClass(stalledDays);
  const stallNudge = getInProgressStallNudge(
    stalledDays,
    appointmentDetails.appointment_status
  );

  return (
    <>
      <article className='flex h-full min-h-0 w-full min-w-0 flex-col overflow-visible rounded-[10px] border border-black/25 bg-white shadow-[0_2px_6px_rgba(0,0,0,0.08)] transition-shadow hover:shadow-[0_4px_16px_rgba(0,0,0,0.13)]'>
        <div
          className={cn(
            'h-1.5 w-full shrink-0 rounded-t-[10px]',
            accentBarClass
          )}
          aria-hidden
        />
        <header className='flex min-w-0 w-full flex-col gap-3 border-b border-gray-200 px-5 py-3.5'>
          <div className='flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:gap-3'>
            <div className='flex flex-col gap-2 flex-1'>
              <h3 className='m-0 w-full max-w-full min-w-0 hyphens-none break-normal font-sans text-base font-semibold leading-snug text-gray-900 sm:min-w-0 sm:max-w-[min(100%,50%)] sm:shrink sm:basis-1/2'>
                {appointmentDetails.carrier}
              </h3>
              {statePills.length > 0 && (
                <div className='flex min-w-0 flex-wrap gap-2'>
                  {statePills.map(state => (
                    <span
                      key={state}
                      className='inline-flex w-fit items-center rounded-full border border-primary-emphasis-light/50 bg-primary-emphasis-light/15 px-3 py-1 text-xs font-semibold text-primary-darkened'
                    >
                      {state}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className='flex min-w-0 w-full max-w-full flex-col items-start gap-2 sm:basis-1/2 sm:shrink sm:items-end'>
              {(stallNudge === 'actSoon' || stallNudge === 'expiring') && (
                <div className='flex w-full max-w-full min-w-0 flex-wrap justify-start gap-2 sm:justify-end'>
                  {stallNudge === 'actSoon' && (
                    <span className='max-w-full shrink rounded-[10px] border border-pending bg-pending/5 px-2.5 py-1 text-sm font-semibold text-pending flex items-center gap-1'>
                      <Clock className='size-3.5 shrink-0' aria-hidden />
                      <span className='pt-0.5'>Act Soon</span>
                    </span>
                  )}
                  {stallNudge === 'expiring' && (
                    <span className='max-w-full shrink rounded-[10px] border border-semantic-danger/50 bg-semantic-danger/5 px-2.5 py-1 text-xs font-semibold text-semantic-danger flex items-center gap-1'>
                      <Clock className='size-3.5 shrink-0' aria-hidden />
                      <span className='pt-0.5'>Expiring Soon</span>
                    </span>
                  )}
                </div>
              )}
              <Tooltip
                title={getSubstatusTooltip(appointmentDetails.substatus)}
                placement='top'
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      padding: '4px 8px',
                      textAlign: 'center',
                      color: '#fff'
                    }
                  }
                }}
              >
                <span
                  className={cn(
                    'block w-fit max-w-full min-w-0 cursor-default rounded-[10px] border border-transparent bg-semantic-danger/15 px-2.5 py-1.5 text-center text-xs font-medium leading-snug wrap-break-word text-semantic-danger',
                    isSubstatusActionable(appointmentDetails.substatus) &&
                      'flex items-center gap-1'
                  )}
                >
                  {isSubstatusActionable(appointmentDetails.substatus) && (
                    <TriangleAlert className='size-3.5 shrink-0' aria-hidden />
                  )}
                  <span
                    className={cn(
                      isSubstatusActionable(appointmentDetails.substatus) &&
                        'pt-0.5'
                    )}
                  >
                    {appointmentDetails.substatus}
                  </span>
                </span>
              </Tooltip>
            </div>
          </div>
        </header>

        <main className='flex flex-1 flex-col gap-2.5 px-5 py-4'>
          <div className='grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2'>
            <div className='flex flex-col gap-0.5'>
              <span className='text-[10px] font-semibold tracking-wide text-gray-400 uppercase'>
                Request Type
              </span>
              <span className='text-sm text-gray-900'>
                {appointmentDetails.request_type}
              </span>
            </div>
            <div className='flex flex-col gap-0.5'>
              <span className='text-[10px] font-semibold tracking-wide text-gray-400 uppercase'>
                Request ID
              </span>
              <span className='text-sm text-gray-900'>
                {appointmentDetails.requestName}
              </span>
            </div>
          </div>

          {hasNote && (
            <>
              <div className='my-0.5 h-px bg-gray-200' />
              <button
                type='button'
                className='inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 py-1 text-left font-semibold text-primary-emphasis-light transition-colors hover:text-primary-darkened'
                onClick={() => setIsNoteExpanded(prev => !prev)}
              >
                <Info className='size-5 shrink-0' />
                <span className='pt-0.5 text-xs font-semibold'>
                  {isNoteExpanded ? 'Hide instructions' : 'View instructions'}
                </span>
                <ChevronIcon
                  className={`inline-block h-4 w-4 shrink-0 transition-transform ${
                    isNoteExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isNoteExpanded && (
                <div className='animate-[slideDown_200ms_ease] border-l-[3px] border-grey-light/50 px-3.5 text-xs text-gray-600 [&_a]:font-medium [&_a]:text-primary-emphasis-light [&_a]:no-underline hover:[&_a]:underline [&_ol]:my-1.5 [&_ol]:pl-[18px] [&_p]:my-1.5 [&_p]:first:mt-0 [&_ul]:my-1.5 [&_ul]:pl-[18px]'>
                  <div
                    className='m-0 leading-relaxed'
                    dangerouslySetInnerHTML={sanitizeDirtyHTML(
                      appointmentDetails.custom_note_for_agent
                    )}
                  />
                  {showNewAppointmentVideo && (
                    <div className='mt-3.5'>
                      <CarrierAppointmentVideo src={newAppointmentsVideoURL} />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!hasNote && showNewAppointmentVideo && (
            <>
              <div className='my-0.5 h-px bg-gray-200' />
              <button
                type='button'
                className='inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 py-1 text-left font-semibold text-primary-emphasis-light transition-colors hover:text-primary-darkened'
                onClick={() => setIsNoteExpanded(prev => !prev)}
              >
                <Info className='size-5 shrink-0' />
                <span className='pt-0.5 text-xs font-semibold'>
                  {isNoteExpanded ? 'Hide video' : 'Show video'}
                </span>
                <ChevronIcon
                  className={`inline-block h-4 w-4 shrink-0 transition-transform ${
                    isNoteExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isNoteExpanded && (
                <div className='border-l-[3px] border-grey-light/50 px-3.5'>
                  <CarrierAppointmentVideo src={newAppointmentsVideoURL} />
                </div>
              )}
            </>
          )}

          <div className='my-0.5 h-px bg-gray-200' />
        </main>

        <footer className='mt-auto flex flex-col gap-2 border-t border-white/10 px-5 py-2.5 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-2'>
          {appointmentDetails.substatus === 'Agent Login Needed' && (
            <Button
              onClick={() => setIsCredentialsModalOpen(true)}
              disabled={isLoading}
              variant='primaryLight'
              text='Submit Credentials'
              size='small'
              className='w-full min-w-0 justify-center bg-[linear-gradient(90deg,var(--color-primary-darkened),var(--color-primary-emphasis-light))] hover:opacity-80 transition-opacity duration-200 sm:w-auto'
            />
          )}
          {actionOptions.map(actionOption => {
            const tooltipSlotProps = {
              tooltip: {
                sx: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  padding: '8px 12px',
                  maxWidth: 320,
                  textAlign: 'center',
                  color: '#fff'
                }
              }
            };
            const actionButton = (
              <Button
                onClick={() => handleActionClick(actionOption)}
                className={cn(
                  'w-full min-w-0 justify-center sm:w-auto',
                  actionOption.value === 'close_request'
                    ? ''
                    : 'bg-[linear-gradient(90deg,var(--color-primary-darkened),var(--color-primary-emphasis-light))] hover:opacity-80 transition-opacity duration-200'
                )}
                disabled={isLoading}
                variant={
                  actionOption.value === 'close_request'
                    ? 'default'
                    : 'primaryLight'
                }
                text={actionOption.button}
                size='small'
              />
            );
            if (!actionOption.tooltip) {
              return (
                <React.Fragment key={actionOption.value}>
                  {actionButton}
                </React.Fragment>
              );
            }
            return (
              <Tooltip
                key={actionOption.value}
                title={actionOption.tooltip}
                placement='top'
                arrow
                slotProps={tooltipSlotProps}
              >
                <span className='inline-flex w-full min-w-0 justify-center sm:w-auto'>
                  {actionButton}
                </span>
              </Tooltip>
            );
          })}
        </footer>
      </article>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <Modal
        active={isCredentialsModalOpen}
        size='medium'
        title={isLoading ? 'Submitting...' : 'Submit Credentials'}
        closeModal={
          isLoading ? () => {} : () => setIsCredentialsModalOpen(false)
        }
      >
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          onSubmit={e =>
            handleSubmit(e, () => setIsCredentialsModalOpen(false))
          }
        >
          <Input
            label='Username'
            type='text'
            value={credentials.username}
            error={errors.username}
            disabled={isLoading}
            onChange={e => handleCredentialChange('username', e.target.value)}
          />
          <Input
            label='Password'
            type='text'
            value={credentials.password}
            error={errors.password}
            disabled={isLoading}
            onChange={e => handleCredentialChange('password', e.target.value)}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <Button
              disabled={isLoading}
              onClick={() => {
                setIsCredentialsModalOpen(false);
                resetCredentialErrors();
              }}
              fill
              text='Cancel'
              variant='danger'
              size='large'
            />
            <Button
              type='submit'
              disabled={isLoading}
              isLoading={isLoading}
              variant='primaryLight'
              text='Submit'
              fill
              size='large'
            />
          </div>
        </form>
      </Modal>

      <Modal
        active={isAcknowledgementOpen}
        title={isLoading ? 'Sending Request...' : 'Acknowledgement'}
        size='large'
        closeModal={resetAcknowledgement}
        showScrollbar
      >
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='space-between'
          height='100%'
          width='100%'
        >
          <Box
            flexGrow={1}
            display='flex'
            flexDirection='column'
            alignItems='stretch'
            marginTop='20px'
            gap={2}
            width='100%'
            px={1}
          >
            <AcknowledgementNotice
              message={action?.acknowledgement}
              isError={!isAcknowledgementAccepted}
              checked={isAcknowledgementAccepted}
              onChange={() =>
                setIsAcknowledgementAccepted(!isAcknowledgementAccepted)
              }
              variant='info'
              disabled={isLoading}
              addBottomMargin={false}
            />

            {action?.value === 'close_request' &&
              Array.isArray(action?.selectOptions) &&
              action.selectOptions.length > 0 && (
                <div className='w-full min-w-0'>
                  <label
                    className='text-sm font-medium text-[var(--color-grey)]'
                    htmlFor='close-request-reason'
                  >
                    {action.selectLabel}
                  </label>
                  <select
                    id='close-request-reason'
                    className='mt-1.5 box-border min-h-11 w-full rounded-md border border-[var(--color-grey-lighter)] bg-[var(--color-input-background)] px-3 py-2 text-sm text-[var(--color-black)] outline-none focus:border-[var(--color-primary-emphasis)] focus:ring-1 focus:ring-[var(--color-primary-emphasis)] disabled:opacity-60'
                    value={closeRequestSelectValue}
                    onChange={e => setCloseRequestSelectValue(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value=''>Select an option</option>
                    {action.selectOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  {!closeRequestSelectValue && (
                    <p className='text-sm text-semantic-danger'>
                      Please select a reason for closing the request.
                    </p>
                  )}

                  {selectedCloseReasonOption && (
                    <BadgeWithIcon
                      className={cn(
                        'mt-4 border',
                        selectedCloseReasonOption.helperBgColor,
                        selectedCloseReasonOption.helperBorderColor
                      )}
                      icon={
                        <div
                          className={cn(
                            'flex size-6 shrink-0 items-center justify-center rounded-full',
                            selectedCloseReasonOption.iconBgColor
                          )}
                          aria-hidden
                        >
                          <Info
                            className='size-3.5 text-white'
                            strokeWidth={2}
                          />
                        </div>
                      }
                    >
                      <p
                        className={cn(
                          'text-sm font-semibold leading-snug',
                          selectedCloseReasonOption.helperTitleColor
                        )}
                      >
                        {selectedCloseReasonOption.helperTitle}
                      </p>
                      <div className='flex flex-col gap-1'>
                        <div
                          dangerouslySetInnerHTML={sanitizeDirtyHTML(
                            selectedCloseReasonOption.helperDescription
                          )}
                          className={cn(
                            'text-sm leading-relaxed',
                            selectedCloseReasonOption.helperDescriptionColor
                          )}
                        />
                        {selectedCloseReasonOption.helperFooter && (
                          <div
                            className={cn(
                              'text-sm leading-relaxed',
                              selectedCloseReasonOption.helperDescriptionColor
                            )}
                            dangerouslySetInnerHTML={sanitizeDirtyHTML(
                              selectedCloseReasonOption.helperFooter
                            )}
                          />
                        )}
                      </div>
                    </BadgeWithIcon>
                  )}
                </div>
              )}
          </Box>
          <Box
            display='flex'
            justifyContent='space-between'
            width='100%'
            mt={2}
            gap='10px'
          >
            <Button
              disabled={isLoading}
              onClick={resetAcknowledgement}
              fill
              text='Cancel'
              variant='danger'
            />
            <Button
              disabled={
                isLoading ||
                !isAcknowledgementAccepted ||
                (action?.value === 'close_request' && !closeRequestSelectValue)
              }
              isLoading={isLoading}
              variant='primaryLight'
              text='Submit'
              fill
              onClick={e => handleSubmit(e, () => {})}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

AppointmentCard.propTypes = {
  appointmentDetails: propTypes.object.isRequired,
  carrierConsidersStates: propTypes.bool.isRequired
};
